<script setup>
const items = ref([{
    label: 'Test the REST endpoint',
    color: "primary",
    class: "text-lg utab-btn",
    variant: "solid",
    defaultOpen: false,
    slot: 'swagger'
}, {
    label: 'Learn how this pipeline works',
    color: "primary",
    class: "text-lg utab-btn",
    variant: "solid",
    defaultOpen: false,
    slot: "information"
}])
</script>

<template>
    <div>
        <div class="flex justify-between items-center flex-col md:flex-row  mt-4 my-8">
            <div class="flex gap-4 self-end">
                <a href="https://drive.google.com/drive/u/1/folders/14YunPWzrQXP3jwxnXQUCljobUYvDmM6W" target="_blank"
                    class="flex items-center gap-4 p-4 bg-white rounded-lg border-2 border-white hover:border-slate-300 transition">
                    <Icon name="logos:google-drive" class="w-10 h-10" />
                    <div class="flex flex-col gap-2">
                        <span class="text-primary-500">
                            <!-- <Icon name="octicon:upload-16" /> Upload -->
                            Browse files
                        </span>
                    </div>
                </a>
            </div>
            <div>

                <a href="https://docs.google.com/spreadsheets/d/1p-f7Pe1FRojCUNg3znJEjpnf1Xoi41dai-BsAhjistU/edit?usp=sharing"
                    target="_blank"
                    class="bg-white block p-4  flex gap-4 items-center mt-4 border-2 hover:border-slate-300 transition rounded-se-lg rounded-es-lg border-primary-500">
                    <div>
                        <NuxtImg src="/assets/cloud/google-sheets.svg" alt="" provider="s3" class="w-10 h-10" />
                    </div>
                    <div>
                        <span class="font-semibold text-[#232a34]">Try Q&A on Contract Documents</span>
                    </div>
                </a>
            </div>
        </div>

        <div class="swagger-content">
            <UAccordion :items="items" :ui="{ wrapper: 'flex flex-col w-full gap-4' }">
                <template #default="{ item, index, open }">
                    <UButton color="gray" variant="ghost"
                        class="border-b text-base border-gray-200 bg-primary-500 text-white hover:text-white hover:bg-primary-600 rounded-xl"
                        :ui="{ rounded: 'rounded-none', padding: { sm: 'p-3' } }">

                        <span class="truncate flex gap-2" v-if="item.slot === 'swagger'">{{ item.label }} <span
                                class="text-xs p-1 px-3 bg-primary-800 border border-primary-400 rounded-full mr-2 block">for
                                developers</span></span>
                        <span class="truncate" v-else>{{ item.label }}</span>

                        <template #trailing>
                            <UIcon name="heroicons:chevron-right-20-solid"
                                class="w-5 h-5 ms-auto transform transition-transform duration-200"
                                :class="[open && 'rotate-90']" />
                        </template>
                    </UButton>
                </template>
                <template #information>
                    <div class="flex flex-col text-[#232a34] text-base">
                        <p>Pathway utilizes RAG (Retrieval-Augmented Generation) to power its document AI magic. Here's
                            a
                            breakdown of the pipeline:</p>
                        <p class="mt-4"><b>1. Document Ingestion:</b><br>
                            Connect your document sources like Google Drive, SharePoint, local storage, or S3.<br>
                            Supported formats include text, PDF, DOCX, and HTML.<br>
                            Documents are NOT duplicated and indexed for efficient retrieval.<br>
                        </p>
                        <p class="mt-4"><b>2. Retrieval Stage:</b><br>
                            When you ask a question, Pathway searches its document index using powerful algorithms.<br>
                            Relevant passages are retrieved based on their potential to answer your query.<br>
                        </p>
                        <p class="mt-4"><b>3. Generation Stage:</b><br>
                            The retrieved passages are fed into a GPT model of choice, fine-tuned for document
                            understanding.<br>
                            GPT model synthesizes the information and generates a clear, concise, and informative answer
                            to
                            your question.<br>
                        </p>
                        <p class="mt-4"><b>4. Summarization (Optional):</b><br>
                            For multiple documents, Pathway can automatically summarize responses, presenting key points
                            for
                            quick review.<br>
                        </p>
                        <p class="mt-4"><b>5. Continuous Learning:</b><br>
                            Pathway constantly learns and adapts. As your documents change, the retrieval and generation
                            models get updated through an always refreshed vector index, ensuring your answers are
                            always
                            based on the latest information.<br>
                        </p>
                        <p class="mt-4"><b>Benefits:</b><br>
                            Accurate & Insightful Answers: Get straight to the point with answers sourced directly from
                            your
                            documents.<br>
                            Effortless Maintenance: No separate data preprocessing needed. No need for a separate vector
                            database. Pathway automatically keeps itself up-to-date.<br>
                            Highly Customizable: Adapt the pipeline to your specific needs, including prompts, search
                            queries, and more.<br>
                            Seamless Integration: Works with various document sources and integrates smoothly with your
                            existing workflows.<br>
                        </p>
                        <div class="mt-8">
                            <a href="https://github.com/pathwaycom/llm-app/tree/main/examples/pipelines/demo-question-answering"
                                class="font-medium flex items-center gap-2 underline hover:text-primary-800 transition max-w-max text-blue-950">
                                <Icon name="mdi:github" /> See the source code
                            </a>
                        </div>
                    </div>
                </template>
                <template #swagger>
                    <LazyCloudSwaggerUI swaggerUrl="https://demo-rag-question-answering.pathway.stream/_schema"
                        uid="swagger-qna-api" />
                </template>
            </UAccordion>
        </div>
    </div>
</template>

<style scoped>
a {
    color: #1C1CF0;
}

a:hover,
a:focus {
    text-decoration: underline;
}

.swagger-content {
    padding-bottom: 5rem;
}

.sharepoint {
    color: #2F7CC2;
}

.underline {
    text-decoration: underline;
}

.button {
    background-color: #dc280b !important;
    border: none;
    border-radius: 0.5em;
    color: #fff !important;
    cursor: pointer;
    font-weight: 700;
    padding: 0.5em 1em;
    transition: background-color .4s, color .4s;
    white-space: nowrap;
    text-decoration: none;
    font-family: sans-serif;
    font-size: 1.125rem;
    line-height: 1.75rem;
    transition: all 0.3s;
    display: inline;
}

.button:hover,
.button:focus {
    background-color: #eee;
    color: #dc280b;
}
</style>
